import React, { useState, useEffect } from 'react';
import { phoneAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import './Catalog.css';

const PLACEHOLDER_IMAGE = "data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27600%27 height=%27600%27%3E%3Crect width=%27600%27 height=%27600%27 fill=%27%23667eea%27/%3E%3Ctext x=%2750%25%27 y=%2750%25%27 font-size=%2732%27 fill=%27white%27 text-anchor=%27middle%27 dominant-baseline=%27middle%27 font-family=%27Arial%27%3ESIN IMAGEN%3C/text%3E%3C/svg%3E";

const Catalog = () => {
  const [phones, setPhones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' o 'list'
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadPhones();
  }, []);

  const loadPhones = async () => {
    try {
      const response = await phoneAPI.getAll();
      setPhones(response.data);
    } catch (error) {
      console.error('Error loading phones:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const filteredPhones = phones.filter(phone => 
    phone.marca.toLowerCase().includes(searchTerm.toLowerCase()) ||
    phone.modelo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (phone.imei && phone.imei.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Paginación
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentPhones = viewMode === 'list' 
    ? filteredPhones.slice(indexOfFirstItem, indexOfLastItem)
    : filteredPhones;
  const totalPages = Math.ceil(filteredPhones.length / itemsPerPage);

  const getGradoText = (grado) => {
    switch(grado) {
      case 'A': return '🟢 A';
      case 'B': return '🟡 B';
      case 'C': return '🔴 C';
      default: return '🟡 B';
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return '/placeholder-phone.png';
    return `${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5003'}${imagePath}`;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Cargando teléfonos...</p>
      </div>
    );
  }

  return (
    <div className="catalog-container">
      <header className="catalog-header">
        <div className="header-content">
          <h1>🏢 La Tabacalera S.A. - Subasta de Teléfonos</h1>
          <div className="header-actions">
            <button className="btn-my-bids" onClick={() => navigate('/my-bids')}>
              💰 Mis Ofertas
            </button>
            {isAdmin() && (
              <button className="btn-admin" onClick={() => navigate('/admin')}>
                ⚙️ Panel Admin
              </button>
            )}
            <button className="btn-logout" onClick={handleLogout}>
              🚪 Salir
            </button>
          </div>
        </div>
        
        <div className="search-controls">
          <div className="search-bar">
            <input
              type="text"
              placeholder="🔍 Buscar por marca, modelo, IMEI o usuario..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
          
          <div className="view-toggle">
            <button 
              className={`btn-view ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => {
                setViewMode('grid');
                setCurrentPage(1);
              }}
              title="Vista de cuadrícula"
            >
              ⊞ Cuadrícula
            </button>
            <button 
              className={`btn-view ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => {
                setViewMode('list');
                setCurrentPage(1);
              }}
              title="Vista de lista"
            >
              ☰ Lista
            </button>
          </div>
        </div>
      </header>

      {viewMode === 'grid' ? (
        <div className="catalog-grid">
          {filteredPhones.length === 0 ? (
            <div className="no-results">
              <p>📱 No se encontraron teléfonos</p>
            </div>
          ) : (
            currentPhones.map((phone) => (
              <div key={phone.id} className="phone-card" onClick={() => navigate(`/phone/${phone.id}`)}>
                <div className="phone-image">
                  <img 
                    src={getImageUrl(phone.primary_image)} 
                    alt={`${phone.marca} ${phone.modelo}`}
                    onError={(e) => {
                      e.target.src = PLACEHOLDER_IMAGE;
                    }}
                  />
                  {phone.vendido && <div className="badge-sold">✅ VENDIDO</div>}
                  {phone.status === 'Dañado' && !phone.vendido && <div className="badge-damaged">⚠️ Dañado</div>}
                </div>
                
                <div className="phone-info">
                  <h3>{phone.marca}</h3>
                  <p className="phone-model">{phone.modelo}</p>
                  
                  <div className="phone-details">
                    {phone.imei && <span className="detail-item">📱 {phone.imei}</span>}
                    <span className="detail-item">🏢 {phone.ubicacion || 'N/A'}</span>
                  </div>
                  
                  <div className={`phone-condition grado-${phone.grado || 'B'}`}>
                    <span className="grado-text">{getGradoText(phone.grado)}</span>
                  </div>
                  
                  {phone.precio_base && !phone.vendido && (
                    <div className="phone-price">
                      💰 RD$ {parseFloat(phone.precio_base).toLocaleString()}
                    </div>
                  )}
                  
                  {phone.vendido && (
                    <div className="phone-sold">
                      ✅ NO DISPONIBLE
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        <>
          <div className="catalog-list">
            {filteredPhones.length === 0 ? (
              <div className="no-results">
                <p>📱 No se encontraron teléfonos</p>
              </div>
            ) : (
              currentPhones.map((phone) => (
                <div key={phone.id} className="phone-list-item" onClick={() => navigate(`/phone/${phone.id}`)}>
                  <div className="list-item-image">
                    <img 
                      src={getImageUrl(phone.primary_image)} 
                      alt={`${phone.marca} ${phone.modelo}`}
                      onError={(e) => {
                        e.target.src = PLACEHOLDER_IMAGE;
                      }}
                    />
                  </div>
                  
                  <div className="list-item-info">
                    <div className="list-item-header">
                      <h3>{phone.marca} {phone.modelo}</h3>
                      {phone.vendido && <span className="badge-sold-small">✅ VENDIDO</span>}
                    </div>
                    
                    <div className="list-item-details">
                      {phone.imei && <span>📱 {phone.imei}</span>}
                      <span>🏢 {phone.ubicacion || 'N/A'}</span>
                      <span className={`grado-badge-small grado-${phone.grado || 'B'}`}>
                        {getGradoText(phone.grado)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="list-item-price">
                    {phone.vendido ? (
                      <span className="sold-text">NO DISPONIBLE</span>
                    ) : phone.precio_base ? (
                      <span className="price-text">RD$ {parseFloat(phone.precio_base).toLocaleString()}</span>
                    ) : (
                      <span className="no-price">Consultar</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
          
          {totalPages > 1 && (
            <div className="pagination">
              <button 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="btn-page"
              >
                ← Anterior
              </button>
              
              <span className="page-info">
                Página {currentPage} de {totalPages}
              </span>
              
              <button 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="btn-page"
              >
                Siguiente →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Catalog;
